import React, { createContext, useContext, useState, useEffect, useCallback } from 'react';
import { useAuthState } from 'react-firebase-hooks/auth';
import { auth } from '../firebaseConfig';

const UserContext = createContext();

export const useUser = () => {
  const context = useContext(UserContext);
  if (!context) {
    throw new Error('useUser must be used within a UserProvider');
  }
  return context;
};

export const UserProvider = ({ children }) => {
  const [user, loading, error] = useAuthState(auth);
  const [userProfile, setUserProfile] = useState(null);
  const [userClubs, setUserClubs] = useState([]);
  const [isLoadingProfile, setIsLoadingProfile] = useState(false);
  const [isNewUser, setIsNewUser] = useState(false);
  const [hasCompletedOnboarding, setHasCompletedOnboardingState] = useState(false);

  const setHasCompletedOnboarding = useCallback((value) => {
    setHasCompletedOnboardingState(value);
    if (value) {
      setUserProfile(prev => prev ? { ...prev, onboardingCompleted: true } : prev);
    }
  }, []);

  const backendUrl = process.env.REACT_APP_BACKEND_URL || 'http://localhost:5000';

  // Helper function to extract all program IDs from user clubs
  const getUserProgramIds = useCallback(() => {
    if (!Array.isArray(userClubs)) return [];
    
    return userClubs.flatMap(membership => 
      membership.club?.programs?.map(p => p._id) || []
    );
  }, [userClubs]);

  // Helper function to get programs by club
  const getProgramsByClub = useCallback(() => {
    if (!Array.isArray(userClubs)) return {};
    
    const programsByClub = {};
    userClubs.forEach(membership => {
      if (membership.club) {
        programsByClub[membership.club._id] = {
          clubName: membership.club.name,
          programs: membership.club.programs || [],
          userRole: membership.role
        };
      }
    });
    
    return programsByClub;
  }, [userClubs]);

  const createUserInBackend = useCallback(async () => {
    if (!user) return;

    try {
      const userData = {
        email: user.email,
        firstName: user.displayName?.split(' ')[0] || '',
        lastName: user.displayName?.split(' ').slice(1).join(' ') || '',
        profilePicture: user.photoURL || null,
        firebaseUid: user.uid,
        roles: ['player'],
        isVerified: user.emailVerified,
        lastLogin: new Date().toISOString()
      };

      const response = await fetch(`${backendUrl}/api/users`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json'
        },
        body: JSON.stringify(userData)
      });

      if (response.ok) {
        const data = await response.json();
        setUserProfile(data.user);
        setIsNewUser(true);
      }
    } catch (error) {
      // Error creating user
    }
  }, [user, backendUrl]);

  const syncUserToBackend = useCallback(async () => {
    if (!user) return;

    setIsLoadingProfile(true);
    try {
      const response = await fetch(`${backendUrl}/api/users?email=${encodeURIComponent(user.email)}`);
      
      if (response.ok) {
        const data = await response.json();
        setUserProfile(data);
        setIsNewUser(false);
      } else if (response.status === 404) {
        await createUserInBackend();
      }
    } catch (error) {
      // Error syncing user
    } finally {
      setIsLoadingProfile(false);
    }
  }, [user, backendUrl, createUserInBackend]);

  const fetchUserClubs = useCallback(async () => {
    if (!user || !userProfile) {
      return;
    }

    try {
      const url = `${backendUrl}/api/clubs/user/${userProfile._id}/memberships`;
      const response = await fetch(url);
      
      if (response.ok) {
        try {
          const data = await response.json();
          
          if (Array.isArray(data)) {
            setUserClubs(data);
          }
        } catch (jsonError) {
          setUserClubs([]);
        }
      } else {
        setUserClubs([]);
      }
    } catch (error) {
      setUserClubs([]);
    }
  }, [user, userProfile, backendUrl]);

  // Fetch user profile when user changes
  useEffect(() => {
    if (user) {
      syncUserToBackend();
    } else {
      setUserProfile(null);
      setUserClubs([]);
    }
  }, [user, syncUserToBackend]);

  // Fetch user clubs when userProfile changes
  useEffect(() => {
    if (user && userProfile) {
      fetchUserClubs();
    }
  }, [user, userProfile, fetchUserClubs]);

  const refreshUserClubs = useCallback(() => {
    fetchUserClubs();
  }, [fetchUserClubs]);

  const refreshUserProfile = useCallback(() => {
    syncUserToBackend();
  }, [syncUserToBackend]);

  // Switch dashboard role (for quick switching from user menu)
  const switchDashboard = useCallback(async (newRole) => {
    if (!userProfile?._id) return { success: false, error: 'No user profile' };
    if (!user) return { success: false, error: 'Not authenticated' };

    try {
      // Get the Firebase auth token
      const token = await user.getIdToken();
      
      // Use the dashboard-role endpoint with addToAvailable to ensure the role is added
      const response = await fetch(`${backendUrl}/api/users/${userProfile._id}/dashboard-role`, {
        method: 'PATCH',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify({ 
          dashboardRole: newRole,
          addToAvailable: true  // Auto-add to available roles when switching
        })
      });

      const data = await response.json();

      if (response.ok && data.success) {
        // Update local userProfile with new dashboard role
        setUserProfile(prev => ({
          ...prev,
          dashboardRole: data.user?.dashboardRole || newRole,
          availableDashboardRoles: data.user?.availableDashboardRoles || prev.availableDashboardRoles
        }));
        
        const roleLabels = {
          player: 'Player Dashboard',
          parent: 'Parent Dashboard',
          club_owner: 'Club Owner Dashboard',
          coach: 'Coach Dashboard'
        };
        
        return { success: true, message: `Switched to ${roleLabels[newRole]}` };
      } else {
        // Ensure error is always a string (API might return object like {code, message})
        let errorMessage = 'Failed to switch dashboard';
        if (typeof data.error === 'string') {
          errorMessage = data.error;
        } else if (data.error?.message) {
          errorMessage = data.error.message;
        } else if (data.message) {
          errorMessage = data.message;
        }
        return { 
          success: false, 
          error: errorMessage
        };
      }
    } catch (error) {
      console.error('Error switching dashboard:', error);
      return { success: false, error: 'Network error' };
    }
  }, [user, userProfile?._id, backendUrl]);

  // Update dashboard role (for account settings - can add new roles)
  const updateDashboardRole = useCallback(async (newRole, addToAvailable = true) => {
    if (!userProfile?._id) return { success: false, error: 'No user profile' };
    if (!user) return { success: false, error: 'Not authenticated' };

    try {
      // Get the Firebase auth token
      const token = await user.getIdToken();
      
      const response = await fetch(`${backendUrl}/api/users/${userProfile._id}/dashboard-role`, {
        method: 'PATCH',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify({ 
          dashboardRole: newRole,
          addToAvailable 
        })
      });

      const data = await response.json();

      if (response.ok && data.success) {
        // Update local userProfile
        setUserProfile(prev => ({
          ...prev,
          dashboardRole: data.user.dashboardRole,
          availableDashboardRoles: data.user.availableDashboardRoles
        }));
        return { success: true };
      } else {
        return { success: false, error: data.error || 'Failed to update dashboard role' };
      }
    } catch (error) {
      console.error('Error updating dashboard role:', error);
      return { success: false, error: 'Network error' };
    }
  }, [user, userProfile?._id, backendUrl]);

  const value = {
    user,
    userProfile,
    userClubs,
    loading: loading || isLoadingProfile,
    error,
    isNewUser,
    hasCompletedOnboarding,
    setHasCompletedOnboarding,
    refreshUserClubs,
    refreshUserProfile,
    syncUserToBackend,
    getUserProgramIds,
    getProgramsByClub,
    // Dashboard role functions
    switchDashboard,
    updateDashboardRole,
  };

  return (
    <UserContext.Provider value={value}>
      {children}
    </UserContext.Provider>
  );
};
